import { RefCallback, useEffect } from 'react'

import { useMultiScroll } from '@/shared/lib'
import { Image } from '@/shared/ui'

// 700px 컨테이너에서 gap(8px)을 제외한 너비 계산
// 3열 그리드: (700 - 16) / 3 = 228px per column
// 2열 그리드: (700 - 8) / 2 = 346px per column
// 1열 그리드: 700 - 0 = 700px

export const DEFAULT_SELECTED_IMAGE_INDEX = -1

const imageSizeMap = {
  large: { width: 464, height: 408 }, // 2/3 너비 (228 * 2)
  medium: { width: 346, height: 408 }, // 1/2 너비
  small: { width: 228, height: 200 }, // 1/3 너비
  full: { width: 700, height: 408 }, // 전체 너비
}

type ImageListProps = {
  images: string[]
  roomName: string
  selectedIndex: number
  onImageClick: (index: number) => void
}

export const ImageList = ({
  images,
  roomName,
  selectedIndex,
  onImageClick,
}: ImageListProps) => {
  const { registerScrollElement, scrollIntoView } = useMultiScroll()

  const slicedImagesByThree = images.reduce<string[][]>((acc, _, index) => {
    if (index % 3 === 0) {
      acc.push(images.slice(index, index + 3))
    }

    return acc
  }, [])

  useEffect(() => {
    if (selectedIndex !== DEFAULT_SELECTED_IMAGE_INDEX) {
      const rowIndex = Math.floor(selectedIndex / 3)
      scrollIntoView(String(rowIndex))
    }
  }, [selectedIndex, scrollIntoView])

  return (
    <div className="flex w-[700px] flex-col gap-2">
      {slicedImagesByThree.map((rowImages, rowIndex) => (
        <ImageRow
          ref={element => {
            if (element) {
              registerScrollElement(rowIndex.toString(), element)
            }
          }}
          key={rowIndex}
          rowImages={rowImages}
          rowIndex={rowIndex}
          startIndex={rowIndex * 3}
          roomName={roomName}
          onImageClick={onImageClick}
        />
      ))}
    </div>
  )
}

type ImageRowProps = {
  rowImages: string[]
  rowIndex: number
  startIndex: number
  roomName: string
  ref: RefCallback<HTMLDivElement>
  onImageClick: (index: number) => void
}

const ImageRow = ({
  rowImages,
  rowIndex,
  startIndex,
  roomName,
  ref,
  onImageClick,
}: ImageRowProps) => {
  const isOddRow = rowIndex % 2 === 0

  const handleImageClick = (index: number) => () => {
    onImageClick(index)
  }

  if (rowImages.length === 1) {
    return (
      <div className="grid h-[408px] grid-cols-1 gap-2" ref={ref}>
        <Image
          src={rowImages[0]}
          alt={`${roomName} ${startIndex}번째 이미지`}
          {...imageSizeMap.full}
          className="cursor-pointer"
          onClick={handleImageClick(startIndex)}
          placeholder
        />
      </div>
    )
  }

  if (rowImages.length === 2) {
    return (
      <div className="grid h-[408px] grid-cols-2 gap-2" ref={ref}>
        {rowImages.map((image, index) => (
          <Image
            key={image}
            src={image}
            alt={`${roomName} ${startIndex + index}번째 이미지`}
            {...imageSizeMap.medium}
            className="cursor-pointer"
            onClick={handleImageClick(startIndex + index)}
            placeholder
          />
        ))}
      </div>
    )
  }

  const gridAreas = isOddRow
    ? "'large large small1' 'large large small2'"
    : "'small1 large large' 'small2 large large'"

  return (
    <div
      className="grid h-[408px] gap-2"
      ref={ref}
      style={{ gridTemplateAreas: gridAreas }}
    >
      <Image
        src={rowImages[0]}
        alt={`${roomName} ${startIndex}번째 이미지`}
        {...(isOddRow ? imageSizeMap.large : imageSizeMap.small)}
        style={{ gridArea: isOddRow ? 'large' : 'small1' }}
        className="cursor-pointer"
        onClick={handleImageClick(startIndex)}
        placeholder
      />
      <Image
        src={rowImages[1]}
        alt={`${roomName} ${startIndex + 1}번째 이미지`}
        {...imageSizeMap.small}
        style={{ gridArea: isOddRow ? 'small1' : 'small2' }}
        className="cursor-pointer"
        onClick={handleImageClick(startIndex + 1)}
        placeholder
      />
      <Image
        src={rowImages[2]}
        alt={`${roomName} ${startIndex + 2}번째 이미지`}
        {...(isOddRow ? imageSizeMap.small : imageSizeMap.large)}
        style={{ gridArea: isOddRow ? 'small2' : 'large' }}
        className="cursor-pointer"
        onClick={handleImageClick(startIndex + 2)}
        placeholder
      />
    </div>
  )
}
